import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  actionTypes,
  alignPositions,
  columnTypes,
  CurrentUser,
  IColumnModel,
  IElementModel,
  IPageModel,
  ITableConfig,
} from '@fantaz/shared/data-access/models';
import { ActivatedRoute, Router } from '@angular/router';
import { UIStore } from '@fantaz/shared/data-access/store';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { take } from 'rxjs';
import { ITimesPlayedInTournament } from '@fantaz/shared/data-access/api';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'fz-shared-table-component',
  templateUrl: './shared-table-component.component.html',
  styleUrls: ['./shared-table-component.component.scss'],
})
export class SharedTableComponentComponent implements OnInit, AfterViewInit {
  display: string[];
  _data: any[];

  @Input()
  loading?: boolean | null = false;

  @Input()
  displayedColumns: IColumnModel[] = [];

  @Input()
  set dataSource(data: any[] | null) {
    // store data
    this._data = data || [];

    // create table datasource
    this.matDataSource = new MatTableDataSource<any>(this._data);
    this.matDataSource.sort = this.sort;
  }

  get dataSource(): any[] {
    return this._data;
  }

  @ViewChild(MatSort)
  sort: MatSort;
  matDataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  @Input()
  configuration: ITableConfig;

  @Input()
  currentUser?: CurrentUser | null;

  @Input()
  isLoggedIn?: boolean | null;

  @Input()
  passesLoaded?: boolean;

  @Input()
  location?: boolean | null;

  @Input()
  locationLoaded?: boolean | null;

  @Input()
  eventsPlayedInfo?: ITimesPlayedInTournament[];

  @Output()
  pageChanged: EventEmitter<IPageModel> = new EventEmitter<IPageModel>();

  @Output()
  elementClicked: EventEmitter<IElementModel> =
    new EventEmitter<IElementModel>();

  @Output()
  playButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  shareButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  @Output()
  detailsButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  customButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  emptyButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  loginButtonPressed: EventEmitter<any> = new EventEmitter<any>();

  page: IPageModel = {
    pageIndex: 0,
    pageSize: 10,
  };
  alignPositions = alignPositions;
  private previousSize = 0;
  highlighted = -1;

  constructor(
    private route: ActivatedRoute,
    private readonly router: Router,
    private readonly uiStore: UIStore,
  ) {}

  ngOnInit(): void {
    this.display = this.displayedColumns.map((column) => column.id);
    if (this.configuration.highlightedRow) {
      this.highlighted = this.matDataSource.data.findIndex(
        (element) => this.configuration.highlightedRow === element.uuid,
      );
    }
    this.uiStore.userPreferences$.pipe(take(1)).subscribe((preferences) => {
      const queryParams = this.route.snapshot.root.queryParams;
      const initPage: IPageModel = {
        pageIndex: 0,
        pageSize: 10,
      };

      if (preferences.pagination.pageSize) {
        initPage.pageSize = preferences.pagination.pageSize;
      }

      if (queryParams[`${this.configuration.pagination?.indexQueryParam}`]) {
        initPage.pageIndex = Number(
          queryParams[`${this.configuration.pagination?.indexQueryParam}`],
        );
      }

      if (queryParams[`${this.configuration.pagination?.sizeQueryParam}`]) {
        initPage.pageSize = Number(
          queryParams[`${this.configuration.pagination?.sizeQueryParam}`],
        );
      }

      if (
        this.configuration.pagination?.showPagination &&
        (initPage.pageIndex !== this.page.pageIndex ||
          initPage.pageSize !== this.page.pageSize)
      ) {
        this.page.pageIndex = initPage.pageIndex;
        this.page.pageSize = initPage.pageSize;
      }
    });
  }

  ngAfterViewInit() {
    this.matDataSource.sort = this.sort;
  }

  onPageChange(page: IPageModel) {
    if (this.previousSize !== page.pageSize) {
      this.previousSize = page.pageSize;
      this.uiStore.setPageSize(page.pageSize);
    }

    const params: any = {};
    params[this.configuration.pagination?.sizeQueryParam || ''] = page.pageSize;
    params[this.configuration.pagination?.indexQueryParam || ''] =
      page.pageIndex;

    if (
      page.pageIndex !== this.page.pageIndex ||
      page.pageSize !== this.page.pageSize
    ) {
      this.page.pageIndex = page.pageIndex;
      this.page.pageSize = page.pageSize;

      this.pageChanged.emit(page);
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  onView(element: IElementModel) {
    this.elementClicked.emit(element);
  }

  get ColumnTypes() {
    return columnTypes;
  }

  get ActionTypes() {
    return actionTypes;
  }

  onDetailsPressed(element: any) {
    this.detailsButtonPressed.emit(element);
  }

  onSharePressed(element: any) {
    this.shareButtonPressed.emit(element);
  }

  onPlayPressed(element: any) {
    this.playButtonPressed.emit(element);
  }

  onCustomPressed(element: any) {
    this.customButtonPressed.emit(element);
  }

  onEmptyButtonPressed() {
    this.emptyButtonPressed.emit();
  }
  onLoginButtonPressed() {
    this.loginButtonPressed.emit();
  }

  get isEmpty() {
    return !(
      this.configuration &&
      this.configuration.pagination &&
      (this.configuration.pagination.paginationTotal as number) > 0
    );
  }
}
